// Signed-in staff member and their access for the current event.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, type User } from 'firebase/auth'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import {
  can as canWith,
  isAccessCurrent,
  platformAdminAccess,
  staffDoc as staffDocPath,
  TOP,
  type Access,
  type Permission,
  type StaffDoc,
  type WithId,
} from '@spark/shared'
import { auth, db, signOutAndWipe } from './firebase.ts'
import { EVENT_ID } from './env.ts'

export interface AuthState {
  user: User | null
  /** True until we know whether someone is signed in and what they may do. */
  loading: boolean
  access: Access | null
  staff: WithId<StaffDoc> | null
  isPlatformAdmin: boolean
  /** Signed in, but with no current staff access to this event. */
  noAccess: boolean
  /** Platform admin or holder of the Administrator role for this event. */
  isAdmin: boolean
  displayName: string
  can: (perm: Permission) => boolean
  canAny: (perms: readonly Permission[]) => boolean
  signIn: (email: string, password: string) => Promise<void>
  /** Sends Firebase's password email; the link returns to `continuePath` (default /login). */
  sendReset: (email: string, continuePath?: string) => Promise<void>
  signOut: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [adminChecked, setAdminChecked] = useState(false)
  const [staff, setStaff] = useState<WithId<StaffDoc> | null>(null)
  const [staffChecked, setStaffChecked] = useState(false)
  const [hadAccess, setHadAccess] = useState(false)

  useEffect(
    () =>
      onAuthStateChanged(auth(), (u) => {
        setUser(u)
        setAuthReady(true)
        setAdminChecked(false)
        setStaffChecked(false)
        setStaff(null)
        setIsPlatformAdmin(false)
      }),
    [],
  )

  useEffect(() => {
    if (!user) return
    let cancelled = false
    getDoc(doc(db(), TOP.platformAdmins, user.uid))
      .then((snap) => !cancelled && setIsPlatformAdmin(snap.exists()))
      .catch(() => !cancelled && setIsPlatformAdmin(false))
      .finally(() => !cancelled && setAdminChecked(true))
    const unsub = onSnapshot(
      doc(db(), staffDocPath(EVENT_ID, user.uid)),
      (snap) => {
        setStaff(snap.exists() ? ({ id: snap.id, ...(snap.data() as StaffDoc) } as WithId<StaffDoc>) : null)
        setStaffChecked(true)
      },
      () => {
        setStaff(null)
        setStaffChecked(true)
      },
    )
    return () => {
      cancelled = true
      unsub()
    }
  }, [user])

  const access: Access | null = useMemo(() => {
    if (isPlatformAdmin) return platformAdminAccess()
    if (!staff) return null
    return { perms: staff.perms, active: staff.active, announceSections: staff.announceSections, roles: staff.roles, accessUntil: staff.accessUntil }
  }, [isPlatformAdmin, staff])

  const loading = !authReady || (!!user && (!adminChecked || !staffChecked))
  const current = isAccessCurrent(access)

  useEffect(() => {
    if (current) setHadAccess(true)
  }, [current])

  // Someone who had access and lost it (deactivated, expired, removed) is signed out right away.
  useEffect(() => {
    if (!loading && user && hadAccess && !current) void signOutAndWipe()
  }, [loading, user, hadAccess, current])

  const can = useCallback((perm: Permission) => canWith(access, perm), [access])
  const canAny = useCallback((perms: readonly Permission[]) => perms.some((p) => canWith(access, p)), [access])

  const value: AuthState = {
    user,
    loading,
    access,
    staff,
    isPlatformAdmin,
    noAccess: !loading && !!user && !current,
    isAdmin: isPlatformAdmin || (access?.roles ?? []).includes('admin'),
    displayName: staff?.displayName || user?.displayName || user?.email || 'Staff',
    can,
    canAny,
    signIn: async (email, password) => {
      await signInWithEmailAndPassword(auth(), email.trim(), password)
    },
    sendReset: async (email, continuePath = '/login') => {
      const path = continuePath.startsWith('/') && !continuePath.startsWith('//') ? continuePath : '/login'
      await sendPasswordResetEmail(auth(), email.trim(), { url: `${window.location.origin}${path}` })
    },
    signOut: () => signOutAndWipe(),
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}

/** Friendly message for Firebase Auth sign-in errors. */
export function authErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code ?? ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'That email and password do not match an account.'
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes or reset your password.'
    case 'auth/user-disabled':
      return 'This account has been turned off. Ask an administrator.'
    case 'auth/network-request-failed':
      return 'No connection. Check the network and try again.'
    case 'auth/multi-factor-auth-required':
      return 'This account needs a second sign-in step, which this site does not support yet.'
    default:
      return 'Sign-in failed. Please try again.'
  }
}
